import React, { useState, useCallback } from 'react';
import styled from 'styled-components';
import { useQuery } from '@tanstack/react-query';
import { Button } from '../../../components/ui/Button';
import { Icon } from '../../../components/ui/Icon';
import type {
  SmartSortingCategorySummary,
  SmartSortingFile,
} from '../../../types/api';
import { getCategoryConfig } from '../lib/categories';
import { queryKeys } from '../../../hooks/useApi';
import { apiService } from '../../../services/api';
import { isSafeApiDownloadUrl } from '../../user-files/lib/downloadUrl';

const Card = styled.section<{ $accentColor: string; $expanded: boolean }>`
  box-sizing: border-box;
  width: 100%;
  min-width: 0;
  background: var(--surface);
  border: 1px solid
    ${(props) => (props.$expanded ? props.$accentColor : 'var(--border)')};
  border-radius: var(--radius-lg);
  transition:
    box-shadow var(--transition-fast),
    border-color var(--transition-fast);
  border-top: 3px solid ${(props) => props.$accentColor};

  &:hover {
    box-shadow: none;
    border-color: ${(props) => props.$accentColor};
  }
`;

const CardToggle = styled.button`
  all: unset;
  box-sizing: border-box;
  width: 100%;
  padding: 1.25rem;
  cursor: pointer;
  text-align: left;

  &:focus-visible {
    outline: 2px solid var(--brand-500);
    outline-offset: 2px;
    border-radius: var(--radius-lg);
  }
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const IconWrapper = styled.span<{ $color: string }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: ${(props) => props.$color};

  svg {
    width: 1.25rem;
    height: 1.25rem;
    stroke-width: 2;
  }
`;

const CardTitle = styled.h3`
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
  color: var(--text-primary);
`;

const CardCount = styled.span<{ $accentColor: string }>`
  margin-left: auto;
  font-size: 1.5rem;
  font-weight: 700;
  color: ${(props) => props.$accentColor};
`;

const ExpandedContent = styled.div`
  min-width: 0;
  margin: 0 1.25rem 1.25rem;
  border-top: 1px solid var(--border);
  padding-top: 0.75rem;
`;

const FileList = styled.ul`
  min-width: 0;
  list-style: none;
  padding: 0;
  margin: 0;
  max-height: 240px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const FileItem = styled.li`
  display: flex;
  min-width: 0;
  align-items: center;
  gap: 0.5rem;
  padding: 0.375rem 0.25rem;
  font-size: 0.825rem;
  border-radius: var(--radius-sm);

  &:hover {
    background: var(--interactive-hover, rgba(0, 0, 0, 0.02));
  }
`;

const FileName = styled.span`
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: var(--text-secondary);
`;

const FileDate = styled.span`
  flex-shrink: 0;
  font-size: 0.75rem;
  color: var(--text-muted);
`;

const DownloadError = styled.p`
  margin: 0 0 var(--space-sm);
  color: var(--danger-600);
  font-size: var(--font-size-sm);
`;

const DownloadButton = styled(Button)`
  flex-shrink: 0;
  padding: var(--space-sm);
`;

const EmptyMessage = styled.p`
  font-size: 0.825rem;
  color: var(--text-muted);
  margin: 0;
  font-style: italic;
`;

const LoadingText = styled.p`
  font-size: 0.8rem;
  color: var(--text-muted);
  margin: 0;
  text-align: center;
  padding: 0.75rem 0;
`;

function formatShortDate(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('es-ES', { day: '2-digit', month: 'short' });
  } catch {
    return '';
  }
}

interface SmartSortingCategoryCardProps {
  category: SmartSortingCategorySummary;
}

export const SmartSortingCategoryCard: React.FC<
  SmartSortingCategoryCardProps
> = ({ category }) => {
  const config = getCategoryConfig(category.key);
  const [expanded, setExpanded] = useState(false);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const IconComponent = config.icon;
  const contentId = React.useId();

  const handleToggle = useCallback(() => {
    setExpanded((prev) => !prev);
  }, []);

  const { data, isLoading, isError } = useQuery<{
    items: SmartSortingFile[];
  }>({
    queryKey: queryKeys.smartSorting.files({ category: category.key }),
    queryFn: () =>
      apiService.getSmartSortingFiles({
        category: category.key,
        per_page: 50,
      }),
    enabled: expanded && category.count > 0,
    staleTime: 30_000,
  });

  const handleDownload = useCallback(async (file: SmartSortingFile) => {
    setDownloadingId(file.id);
    setDownloadError(null);
    try {
      const response = await apiService.downloadSmartSortingFile(file.id);
      if (!isSafeApiDownloadUrl(response.presigned_get_url)) {
        throw new Error('El enlace de descarga no es seguro.');
      }
      window.open(response.presigned_get_url, '_blank', 'noopener,noreferrer');
    } catch (error) {
      setDownloadError(
        error instanceof Error
          ? error.message
          : 'No se pudo descargar el archivo.',
      );
    } finally {
      setDownloadingId(null);
    }
  }, []);

  return (
    <Card $accentColor={config.color} $expanded={expanded}>
      <CardToggle
        type="button"
        onClick={handleToggle}
        aria-expanded={expanded}
        aria-controls={contentId}
        aria-label={`${category.label} — ${category.count} archivos`}
      >
        <CardHeader>
          <IconWrapper $color={config.color} aria-hidden>
            <IconComponent />
          </IconWrapper>
          <CardTitle>{category.label}</CardTitle>
          <CardCount $accentColor={config.color}>{category.count}</CardCount>
        </CardHeader>
      </CardToggle>

      {expanded && (
        <ExpandedContent id={contentId}>
          {downloadError && (
            <DownloadError role="alert">{downloadError}</DownloadError>
          )}
          {category.count === 0 ? (
            <EmptyMessage>Sin archivos clasificados</EmptyMessage>
          ) : isLoading ? (
            <LoadingText>Cargando archivos...</LoadingText>
          ) : isError ? (
            <EmptyMessage>No se pudieron cargar los archivos.</EmptyMessage>
          ) : data && data.items.length > 0 ? (
            <FileList>
              {data.items.map((file) => (
                <FileItem key={file.id}>
                  <FileName title={file.original_filename}>
                    {file.original_filename}
                  </FileName>
                  <FileDate>{formatShortDate(file.created_at)}</FileDate>
                  <DownloadButton
                    variant="tertiary"
                    size="sm"
                    loading={downloadingId === file.id}
                    disabled={
                      downloadingId !== null && downloadingId !== file.id
                    }
                    onClick={() => handleDownload(file)}
                    aria-label={`Descargar ${file.original_filename}`}
                    title={`Descargar ${file.original_filename}`}
                  >
                    <Icon name="download" aria-hidden />
                  </DownloadButton>
                </FileItem>
              ))}
            </FileList>
          ) : (
            <EmptyMessage>Sin archivos clasificados</EmptyMessage>
          )}
        </ExpandedContent>
      )}
    </Card>
  );
};
